import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, Upload, Download, Loader2 } from "lucide-react";
import { PageShell } from "@/components/SidebarLayout";
import HorizonSelector from "@/components/HorizonSelector";
import { useAuth } from "@/auth/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { formatCr } from "@/lib/format";

/**
 * Shared editable Category × Month grid used by all 5 Outflow pages.
 * Backend: /api/outflows/{page}. Role gate: only page's owning role + admin
 * can save / add / delete / import. Everyone can view + export.
 */
export default function OutflowGrid({ page, kicker, title, testId, editRole, description }) {
  const { user } = useAuth();
  const canEdit = editRole === "any" || user?.role === "admin" || user?.role === editRole;

  const [horizon, setHorizon] = useState(18);
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState({}); // {cat: {month: amount}}
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/outflows/${page}`, { params: { horizon } });
      setData(data);
      setDirty({});
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    }
  }, [page, horizon]);

  useEffect(() => { load(); }, [load]);

  const merged = useMemo(() => {
    if (!data) return null;
    const m = {};
    for (const c of data.categories) {
      m[c] = { ...(data.matrix[c] || {}) };
      if (dirty[c]) Object.assign(m[c], dirty[c]);
    }
    return m;
  }, [data, dirty]);

  const rowTotals = useMemo(() => {
    if (!merged) return {};
    const t = {};
    for (const c of Object.keys(merged)) t[c] = Object.values(merged[c]).reduce((a, b) => a + (parseFloat(b) || 0), 0);
    return t;
  }, [merged]);

  const colTotals = useMemo(() => {
    if (!merged || !data) return {};
    const t = {};
    for (const m of data.months) t[m] = 0;
    for (const c of Object.keys(merged)) for (const m of data.months) t[m] += parseFloat(merged[c][m]) || 0;
    return t;
  }, [merged, data]);

  const grand = useMemo(() => Object.values(rowTotals).reduce((a, b) => a + b, 0), [rowTotals]);

  const setCell = (cat, month, val) => {
    const num = val === "" ? 0 : parseFloat(val);
    if (Number.isNaN(num)) return;
    setDirty((d) => ({ ...d, [cat]: { ...(d[cat] || {}), [month]: num } }));
  };

  const save = async () => {
    if (Object.keys(dirty).length === 0) return;
    setSaving(true);
    try {
      await api.post(`/outflows/${page}/bulk`, { entries: dirty });
      toast.success("Saved");
      await load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  // Auto-save a category's edited cells when the user leaves a cell — so
  // switching tabs never loses input. On success the values are merged into
  // `data` (no full reload → focus/scroll preserved); on failure the cells
  // stay dirty and the Save button remains as the retry path.
  const saveDirtyCategory = async (cat) => {
    const cells = dirty[cat];
    if (!cells || Object.keys(cells).length === 0) return;
    setAutoSaving(true);
    try {
      await api.post(`/outflows/${page}/bulk`, { entries: { [cat]: cells } });
      setData((prev) => {
        if (!prev) return prev;
        const matrix = { ...prev.matrix, [cat]: { ...(prev.matrix[cat] || {}) } };
        for (const [m, v] of Object.entries(cells)) matrix[cat][m] = v;
        return { ...prev, matrix };
      });
      setDirty((d) => { const n = { ...d }; delete n[cat]; return n; });
    } catch (e) {
      toast.error("Auto-save failed — click Save to retry", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setAutoSaving(false);
    }
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await api.post(`/outflows/${page}/category`, { name: newCat.trim() });
      toast.success(`Added ${newCat.trim()}`);
      setNewCat("");
      setShowAdd(false);
      await load();
    } catch (e) {
      toast.error("Add failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const removeCategory = async (name) => {
    if (!window.confirm(`Delete row "${name}" and all its entries?`)) return;
    try {
      await api.delete(`/outflows/${page}/category/${encodeURIComponent(name)}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data: res } = await api.post(`/outflows/${page}/import`, form);
      const fmt = res.format ? ` · ${res.format}-format` : "";
      toast.success(`Imported ${res.entries} cells${fmt}`, {
        description: res.new_categories?.length ? `+ ${res.new_categories.length} new row${res.new_categories.length === 1 ? "" : "s"}` : undefined,
      });
      await load();
    } catch (er) {
      toast.error("Import failed", { description: formatApiError(er.response?.data?.detail) });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const doExport = async () => {
    try {
      const url = `/exports/outflows/${page}.xlsx?horizon=${horizon}`;
      const { data: blob } = await api.get(url, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([blob], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      link.download = `outflows_${page}_${horizon}m.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error("Export failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  return (
    <PageShell
      testId={testId}
      kicker={kicker}
      title={title}
      hideProjectFilter
      rangeLabel={`${horizon}m`}
      rangeMonths={data?.months}
      actions={
        <div className="flex items-center gap-2">
          <HorizonSelector value={horizon} onChange={setHorizon} months={data?.months} />
          <button
            data-testid={`${testId}-export`}
            onClick={doExport}
            className="cf-touch inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-surface text-ink text-xs font-medium border border-hairline hover:bg-surface-2"
          >
            <Download size={12} /> Export
          </button>
          {canEdit && (
            <>
              <input ref={fileRef} type="file" accept=".xlsx" onChange={onImport} className="hidden" data-testid={`${testId}-import-input`} />
              <button
                data-testid={`${testId}-import`}
                disabled={importing}
                onClick={() => fileRef.current?.click()}
                className="cf-touch inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-surface text-ink text-xs font-medium border border-hairline hover:bg-surface-2 disabled:opacity-50"
              >
                {importing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Import Excel
              </button>
              {autoSaving && (
                <span data-testid={`${testId}-autosaving`} className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">Saving…</span>
              )}
              <button
                data-testid={`${testId}-save`}
                disabled={saving || Object.keys(dirty).length === 0}
                onClick={save}
                className="cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-ink text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
              </button>
            </>
          )}
        </div>
      }
    >
      <p className="-mt-2 text-muted-dark text-sm max-w-3xl">
        {description}
        {canEdit && (
          <span className="ml-2 text-[10.5px] text-muted-light">
            · Import Excel accepts either wide (Category × month) or long (Category | Month | ₹L) sheets.
          </span>
        )}
        {!canEdit && (
          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold" style={{ background: "var(--cf-accent-soft)", color: "var(--cf-accent-deep)" }}>
            View-only · Editable by {editRole}
          </span>
        )}
      </p>

      <div className="cf-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted-light border-b border-hairline/70">
                <th className="text-left font-semibold px-4 py-3 sticky left-0 z-10" style={{ background: "var(--cf-surface)", minWidth: 220 }}>Category</th>
                {data?.months.map((m, i) => (
                  <th key={m} className="text-right font-semibold px-2 py-3 min-w-[72px]">{data.month_labels[i]}</th>
                ))}
                <th className="text-right font-semibold px-3 py-3 sticky right-0" style={{ background: "var(--cf-accent-solid)", color: "var(--cf-accent-deep)" }}>Total</th>
                {canEdit && <th className="px-2 py-3" style={{ background: "var(--cf-surface)" }}></th>}
              </tr>
            </thead>
            <tbody>
              {data?.categories.map((cat) => (
                <tr key={cat} data-testid={`${testId}-row-${cat}`} className="border-b border-hairline/40 hover:bg-surface-2/40">
                  <td className="px-4 py-2 font-medium text-ink sticky left-0" style={{ background: "var(--cf-surface)", minWidth: 220 }}>{cat}</td>
                  {data.months.map((m) => (
                    <td key={m} className="px-1 py-1 text-right num">
                      {canEdit ? (
                        <input
                          data-testid={`${testId}-cell-${cat}-${m}`}
                          type="number"
                          step="0.01"
                          value={(merged?.[cat]?.[m] ?? "") === 0 ? "" : merged?.[cat]?.[m]}
                          onChange={(e) => setCell(cat, m, e.target.value)}
                          onBlur={() => saveDirtyCategory(cat)}
                          placeholder="0"
                          className="w-full min-w-[62px] px-1.5 py-1 bg-transparent text-right text-xs num focus:bg-surface focus:outline-none focus:ring-1 focus:ring-coral/40 rounded"
                        />
                      ) : (
                        <span className="text-muted-dark">{merged?.[cat]?.[m] ? formatCr(merged[cat][m]) : "–"}</span>
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right num font-semibold sticky right-0" style={{ background: "var(--cf-accent-solid)", color: "var(--cf-accent-deep)" }}>
                    {rowTotals[cat] ? formatCr(rowTotals[cat]) : "–"}
                  </td>
                  {canEdit && (
                    <td className="px-2 py-2" style={{ background: "var(--cf-surface)" }}>
                      <button data-testid={`${testId}-delete-${cat}`} onClick={() => removeCategory(cat)} className="text-muted-light hover:text-red-500">
                        <Trash2 size={12} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-hairline">
                <td className="px-4 py-3 font-bold text-ink sticky left-0" style={{ background: "var(--cf-surface)", minWidth: 220 }}>Total</td>
                {data?.months.map((m) => (
                  <td key={m} className="px-2 py-3 text-right num font-semibold">
                    {colTotals[m] ? formatCr(colTotals[m]) : "–"}
                  </td>
                ))}
                <td className="px-3 py-3 text-right num font-bold sticky right-0" style={{ background: "var(--cf-accent-solid)", color: "var(--cf-accent-deep)" }}>
                  {formatCr(grand)}
                </td>
                {canEdit && <td></td>}
              </tr>
            </tfoot>
          </table>
        </div>

        {canEdit && (
          <div className="px-4 py-3 border-t border-hairline">
            {showAdd ? (
              <div className="flex items-center gap-2">
                <input
                  data-testid={`${testId}-new-cat`}
                  autoFocus
                  value={newCat}
                  onChange={(e) => setNewCat(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addCategory()}
                  placeholder="New category name"
                  className="flex-1 px-3 py-2 bg-surface rounded-lg text-sm"
                />
                <button data-testid={`${testId}-new-cat-save`} onClick={addCategory} className="px-3 py-2 rounded-full bg-ink text-white text-xs font-medium">Add</button>
                <button onClick={() => { setShowAdd(false); setNewCat(""); }} className="px-3 py-2 text-xs text-muted-dark">Cancel</button>
              </div>
            ) : (
              <button data-testid={`${testId}-new-cat-btn`} onClick={() => setShowAdd(true)} className="cf-touch inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium text-muted-dark hover:text-ink">
                <Plus size={12} /> Add row
              </button>
            )}
          </div>
        )}
      </div>
    </PageShell>
  );
}
